import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { toTitleCase, stripBrandSuffix } from '../lib/titleCase';
import Breadcrumb from '../src/components/ui/breadcrumb';
import { generateBlogBreadcrumbs } from '../lib/breadcrumbs';
import PostCoverImage from '../src/components/blog/PostCoverImage';
import ImageLightbox from '../src/components/ui/ImageLightbox';
import RelatedPosts, { RelatedPostsSkeleton } from '../src/components/blog/RelatedPosts';
import BlogCtaCard from '../src/components/blog/BlogCtaCard';
import ShareButton from '../src/components/blog/ShareButton';
import { BLOG_ROBOTS, DEFAULT_OG_IMAGE, type PageMetadata } from '../lib/seo';
import { PageContainer } from '../src/components/ui/page-container';
import { Button } from '../src/components/ui/button';
import { MetaText } from '../src/components/ui/typography';
import { EmptyState } from '../src/components/ui/empty-state';
import { Skeleton } from '../src/components/ui/skeleton';
import { getPostBySlug } from '../services/blogService';
import { toast } from '../src/lib/toast';
import { calculateReadTime } from '../lib/readTime';
import { sanitizeBlogBody } from '../lib/sanitizeBlogHtml';
import { getSSGPageData } from '../lib/ssGData';
import { toSeededFullPost } from '../lib/blogSeed';
import type { BlogCategory, HydratedBlogPost } from '../src/types/blog';
import { CATEGORY_LABELS, isBlogCategory } from '../src/constants/blog';
import { formatDate } from '../lib/formatDate';

const buildPostMetadata = (
    post: HydratedBlogPost,
    category: BlogCategory,
    slug: string
): PageMetadata => {
    // stripBrandSuffix removes the pipeline's embedded "| Martial Arts Network" from seo_title
    // so the " – Martial Arts Network" appended below isn't doubled (shared with the SSG).
    const headline = stripBrandSuffix(toTitleCase(post.seo_title ?? post.title));
    const url = `https://martialartnetwork.com.au/blog/${category}/${slug}`;
    return {
        title: `${headline} – Martial Arts Network`,
        description: post.excerpt,
        canonical: url,
        robots: BLOG_ROBOTS,
        ogTitle: headline,
        ogDescription: post.excerpt,
        ogImage: post.featured_image_url || DEFAULT_OG_IMAGE,
        ogUrl: url,
        ogType: 'article',
    };
};

// Mirrors the loaded article's narrow column structure + vertical rhythm so the
// skeleton→loaded swap doesn't shift: breadcrumb → title (2 lines) → deck (2
// lines) → [date·read-time + Share button row] → 16:9 rounded cover → body →
// CTA banner.
// The related-posts row is a separate wider sibling (RelatedPostsSkeleton),
// rendered alongside this in BlogPostPage's loading branch.
const PostSkeleton: React.FC = () => (
    <div role="status" aria-label="Loading article" aria-busy="true">
      <div aria-hidden="true">
        {/* Breadcrumb */}
        <Skeleton className="h-4 w-48" />

        {/* Header — title / deck / meta / cover, matching the loaded mt-4 rhythm */}
        <div className="mt-4">
            <Skeleton className="h-9 lg:h-12 w-3/4" />
            <Skeleton className="h-9 lg:h-12 w-1/2 mt-2" />
            <Skeleton className="h-5 w-full mt-4" />
            <Skeleton className="h-5 w-5/6 mt-2" />
            {/* Meta row — date·read-time (left) + Share button (right), matching the loaded layout */}
            <div className="mt-4 mb-8 flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-11 w-24 rounded-md" />
            </div>
            <Skeleton className="aspect-video w-full rounded-2xl" />
        </div>

        {/* Body paragraphs (matches the prose body's mt-12 + centered max-w-2xl) */}
        <div className="mt-12 space-y-3 max-w-2xl mx-auto">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
        </div>

        {/* CTA banner (matches BlogCtaCard's mt-16 + rounded-2xl footprint) */}
        <Skeleton className="mt-16 h-44 w-full rounded-2xl" />
      </div>
    </div>
);

type FetchState =
    | { status: 'loading' }
    | { status: 'not-found' }
    | { status: 'error' }
    | { status: 'loaded'; post: HydratedBlogPost };

const BlogPostPage: React.FC = () => {
    const { category, slug } = useParams<{ category: string; slug: string }>();

    const validCategory = isBlogCategory(category) ? category : null;

    // UI-06: the build-injected full post for THIS route, or null. During the
    // SSG pass this is what turns the prerendered page from a skeleton into the
    // article (h1 + body baked into the HTML crawlers index). In the browser it
    // gives a zero-fetch, zero-flicker first paint. Route-scoped inside
    // toSeededFullPost (category+slug must both match), because the __SSG_DATA__
    // tag survives client-side navigation.
    const seededPost = useMemo(
        () => toSeededFullPost(getSSGPageData()?.blogPost, validCategory, slug),
        [validCategory, slug],
    );

    const [state, setState] = useState<FetchState>(() =>
        seededPost ? { status: 'loaded', post: seededPost } : { status: 'loading' });
    const [zoomOpen, setZoomOpen] = useState(false);

    // Re-invocable fetch — called on mount/route-change and by the error-state
    // "Try again" retry. The request-id guard ignores stale resolutions
    // (unmount or rapid route change), replacing the previous per-effect
    // `cancelled` flag. On failure: distinct 'error' state + toast (item 44,
    // mirroring item 25's intent on the index/category pages). A genuine
    // not-found still renders the existing 404 EmptyState.
    const requestIdRef = useRef(0);
    const loadPost = useCallback(() => {
        if (!slug || !validCategory) {
            setState({ status: 'not-found' });
            return;
        }
        const requestId = ++requestIdRef.current;
        setState({ status: 'loading' });
        getPostBySlug(slug)
            .then((post) => {
                if (requestId !== requestIdRef.current) return;
                if (!post || post.category !== validCategory) {
                    setState({ status: 'not-found' });
                    return;
                }
                setState({ status: 'loaded', post });
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) return;
                setState({ status: 'error' });
                toast.error("Couldn't load this post. Please try again.");
            });
    }, [slug, validCategory]);

    useEffect(() => {
        // UI-06: a seeded route does not fetch AT ALL — the deploy is what
        // publishes a post, so the deployed body is the truth (the same owner
        // decision the listing pages already encode). Restoring state here (not
        // only in useState) covers client-side RETURN navigation to the seeded
        // route, where the initialiser doesn't re-run but the payload is still
        // this route's. Non-seeded routes (dev, client navigation to other
        // posts) fetch exactly as before.
        if (seededPost) {
            // Review fix: invalidate any in-flight fetch BEFORE restoring. Without
            // this, navigating seeded-A → B (fetch starts) → Back to A (restore,
            // no fetch) left B's request live with a still-current requestId; its
            // late resolution then replaced A's article with B's — permanently,
            // because seeded routes never fetch to self-correct.
            requestIdRef.current += 1;
            setState({ status: 'loaded', post: seededPost });
            return;
        }
        loadPost();
    }, [loadPost, seededPost]);

    // Memoise read-time so it's computed once per post-body change rather than
    // on every render. `body` narrows the discriminated union — null during
    // loading/not-found, the HTML string once loaded.
    const body = state.status === 'loaded' ? state.post.body : null;
    const readTime = useMemo(
        () => (body ? calculateReadTime(body) : 0),
        [body]
    );

    return (
        <div className="min-h-screen bg-surface-canvas">
            {state.status === 'loaded' && validCategory && (
                <SeoHead
                    meta={buildPostMetadata(
                        state.post,
                        validCategory,
                        slug ?? state.post.slug
                    )}
                />
            )}
            <div className="h-16" />
            <main id="main-content" className="text-text-primary pb-12">
                <PageContainer size="narrow" className="pt-12">
                    {state.status === 'loading' && <PostSkeleton />}

                    {state.status === 'not-found' && (
                        <EmptyState
                            headingLevel={1}
                            title="Post not found"
                            description="The post you're looking for doesn't exist or has been removed."
                            action={
                                <Button variant="secondary" asChild>
                                    <Link to="/blog">← Back to Blog</Link>
                                </Button>
                            }
                        />
                    )}

                    {state.status === 'error' && (
                        <EmptyState
                            headingLevel={1}
                            title="Couldn't load post"
                            description="Something went wrong. Please try again."
                            action={
                                <Button variant="secondary" onClick={loadPost}>
                                    Try again
                                </Button>
                            }
                        />
                    )}

                    {state.status === 'loaded' && (
                        <article>
                            {/* Visible trail from the shared spine (lib/breadcrumbs.ts) — the
                                SSG derives this post's BreadcrumbList from the SAME builder
                                with the SAME labels, so what a reader sees and what Google
                                reads cannot drift. selfPath is omitted here: the JSON-LD (and
                                with it the leaf's self URL) is emitted by the SSG only. */}
                            <Breadcrumb
                                items={generateBlogBreadcrumbs(
                                    state.post.category,
                                    CATEGORY_LABELS[state.post.category],
                                    toTitleCase(state.post.title),
                                )}
                            />

                            <header>
                                {/* H1 — 48px desktop, responsive down to 30px mobile. Local override of PageTitle for blog hero typography. */}
                                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-primary tracking-tight leading-tight">
                                    {toTitleCase(state.post.title)}
                                </h1>
                                {/* Deck — tightly coupled to the title (mt-4), muted, regular weight. */}
                                <p className="mt-4 text-lg sm:text-xl text-text-muted leading-relaxed font-normal">
                                    {state.post.excerpt}
                                </p>
                                {/* Metadata strip — date · read-time on the left, Share on the right. */}
                                <div className="mt-4 mb-8 flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <time dateTime={state.post.published_at.toISOString().split('T')[0]}>
                                            <MetaText>{formatDate(state.post.published_at)}</MetaText>
                                        </time>
                                        <span aria-hidden="true" className="text-sm text-text-muted">·</span>
                                        <MetaText>{readTime} min read</MetaText>
                                    </div>
                                    <ShareButton
                                        title={toTitleCase(state.post.title)}
                                        url={`https://martialartnetwork.com.au/blog/${state.post.category}/${slug ?? state.post.slug}`}
                                        className="shrink-0"
                                    />
                                </div>
                                {/* Hero hover-zooms (group) AND click opens the shared ImageLightbox,
                                    akin to the gym-profile images — only when there's a real featured
                                    image (the BeltIcon placeholder stays non-interactive). */}
                                {/* UI-13: kept as raw <button> — bespoke photo-tile opener (established
                                    justified class, same as the gym-photo openers): block-level w-full
                                    image wrapper with cursor-zoom-in, rounded-2xl and a ring-2 focus
                                    treatment; Button's inline-flex sizing/padding/radius base would all
                                    need overriding, adding noise rather than consistency. */}
                                {state.post.featured_image_url ? (
                                    <button
                                        type="button"
                                        onClick={() => setZoomOpen(true)}
                                        aria-label="View full-size image"
                                        className="group block w-full cursor-zoom-in rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                                    >
                                        <PostCoverImage post={state.post} loading="eager" className="rounded-2xl" />
                                    </button>
                                ) : (
                                    <PostCoverImage post={state.post} loading="eager" className="rounded-2xl" />
                                )}
                            </header>

                            {zoomOpen && state.post.featured_image_url && (
                                <ImageLightbox
                                    src={state.post.featured_image_url}
                                    alt={state.post.title}
                                    onClose={() => setZoomOpen(false)}
                                />
                            )}

                            {/* Body: constrain the reading measure (~80ch via max-w-2xl) and centre it
                                (mx-auto) under the wider cover/header — the unbounded max-w-none ran
                                ~106ch. break-words guards long URLs; prose-pre:overflow-x-auto keeps a
                                wide code block from pushing the page wide; brand-coloured links. */}
                            <div
                                className="prose prose-slate max-w-2xl mx-auto break-words mt-12 prose-pre:overflow-x-auto prose-a:text-brand-primary"
                                dangerouslySetInnerHTML={{ __html: sanitizeBlogBody(state.post.body) }}
                            />

                            {/* Point-in-time notice (operator-ruled 2026-10-11): every post, never /blog
                                or a category page — so it lives here, not in the shared BlogCtaCard. The
                                date text's classes (MetaText) plus italic, on the body's measure. Static
                                text with no date in it, so the prerender and hydration render agree. */}
                            <p className="mt-12 max-w-2xl mx-auto text-sm font-normal leading-[1.5] text-text-muted italic">
                                This post describes Martial Arts Network as it was when it was published. Listings, figures and features may have changed since.
                            </p>

                            <BlogCtaCard post={state.post} />
                        </article>
                    )}
                </PageContainer>

                {/* RelatedPosts is mounted as a SIBLING of the article PageContainer
                    (not nested inside it) so it can inhabit the wider max-w-7xl grid
                    via its own PageContainer, matching /blog and /blog/:category
                    listing-grid width. The article column above stays at max-w-4xl.
                    During the post fetch, a matching-width skeleton stands in. */}
                {state.status === 'loading' && <RelatedPostsSkeleton />}
                {state.status === 'loaded' && (
                    <RelatedPosts post={state.post} />
                )}
            </main>
        </div>
    );
};

export default BlogPostPage;
